/**
 * THE AREAS. One list, for the whole backend.
 *
 * 2026-08-30: "we use routes and areas at a lot of places so dont mess those up
 * everywhere we use areas make sure these new ones are covered".
 *
 * Before this there were three copies on the server and ten more in the
 * portal — selectable options, colour maps, the driver route groups (twice,
 * in routingService.js and drivers.js) and a priority order. Adding an area
 * meant finding all thirteen, and missing the route-group copy meant the new
 * area's bookings quietly defaulted onto the Downtown car.
 *
 * ROUTE GROUP IS THE FIELD THAT MATTERS. It decides which driver a booking's
 * stops are seeded onto (routingService.generateStops). An area missing from it
 * does not fail loudly — it falls through to Downtown and somebody drives
 * across the city.
 *
 * The server reads the same list from routes/services/areas.js. The two files
 * are deliberately identical in content — the portal cannot import from the
 * backend tree — and areas.test.js fails if they drift apart.
 */

const AREAS = [
    // area                    colour     route group   label (if it differs)
    { name: 'Downtown Dubai',        color: '#4c7ac5', group: 'Downtown' },
    { name: 'Business Bay',          color: '#4c7ac5', group: 'Downtown' },
    { name: 'Creek Harbour',         color: '#476f37', group: 'Downtown' },
    { name: 'Meydan',                color: '#a43777', group: 'Downtown' },
    // Added 2026-08-30. Both sit beside Business Bay and the creek, so they
    // ride with Downtown. D3 shares Meydan's colour by request — they are next
    // to each other and read as one part of town.
    { name: 'Dubai Design District', color: '#a43777', group: 'Downtown' },
    { name: 'Al Jadaf',              color: '#2f8f5b', group: 'Downtown' },

    { name: 'Marina',                color: '#ae2021', group: 'Marina' },
    { name: 'JLT',                   color: '#ae2021', group: 'Marina' },
    { name: 'JBR',                   color: '#ae2021', group: 'Marina' },
    { name: 'Palm Jumeirah',         color: '#d9b342', group: 'Marina' },
    { name: 'JVC',  label: 'JVC (Jumeirah Village Circle)',   color: '#683d1e', group: 'Marina' },
    { name: 'JVT',  label: 'JVT (Jumeirah Village Triangle)', color: '#683d1e', group: 'Marina' },
    { name: 'Dubai Hills',           color: '#683d1e', group: 'Marina' },
    { name: 'Dubai Sports City',     color: '#683d1e', group: 'Marina' },
    { name: 'The Greens',            color: '#683d1e', group: 'Marina' },
    // Added 2026-08-30, out by JVC and Sports City, so they ride with Marina.
    { name: 'Al Barsha South',       color: '#683d1e', group: 'Marina' },
    { name: 'Arjan',                 color: '#683d1e', group: 'Marina' },

    // The catch-all. Kept because 17 properties still sit in it, and it must
    // stay routable — Downtown, as it always was.
    { name: 'Other Area',            color: '#6c757d', group: 'Downtown' },
];

const AREA_NAMES = AREAS.map((a) => a.name);

/** group name → its areas. What routingService seeds stops with. */
const ROUTE_GROUPS = AREAS.reduce((m, a) => {
    (m[a.group] = m[a.group] || []).push(a.name);
    return m;
}, {});

const groupOf = (area) => AREAS.find((a) => a.name === area)?.group || null;
const colorOf = (area) => AREAS.find((a) => a.name === area)?.color || '#6c757d';

/**
 * The order a day is worked in: the far side of town first, so the driver ends
 * up back where the depot is. Marina's areas, then Downtown's.
 */
const AREA_PRIORITY = [
    ...ROUTE_GROUPS.Marina,
    ...ROUTE_GROUPS.Downtown.filter((a) => a !== 'Other Area'),
];

/** react-select options, in the order the list is written. */
export const AREA_OPTIONS = AREAS.map((a) => ({ value: a.name, label: a.label || a.name }));

export { AREAS, AREA_NAMES, ROUTE_GROUPS, AREA_PRIORITY, groupOf, colorOf };
